import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as api from '../api/client'
import type { EventInfo } from '../types'

/**
 * Compact header badge + slide-out drawer + transient toast for a team's
 * event feed. Replaces the old full-width bottom ticker.
 *
 * Badge lives in the header; click to open drawer. Drawer and toasts are
 * fixed-positioned overlays so they escape the header's layout.
 *
 * "Seen" state persists in localStorage keyed by (sessionId, teamId), so
 * the unread pulse survives refreshes.
 */
export default function EventsPanel({
  sessionId, teamId,
}: { sessionId: string; teamId: string }) {
  const catalogQ = useQuery({
    queryKey: ['events'],
    queryFn: api.getEventsCatalog,
    staleTime: 5 * 60_000,
  })
  const historyQ = useQuery({
    queryKey: ['session-events', sessionId],
    queryFn: () => api.getSessionEvents(sessionId),
    refetchInterval: 10_000,
  })

  const catalog = catalogQ.data ?? []
  const byId = useMemo(
    () => Object.fromEntries(catalog.map((e) => [e.id, e])),
    [catalog],
  )

  // Flatten team history, newest-first.
  const rows = useMemo(() => {
    const raw = (historyQ.data ?? []).filter((h) => h.team_id === teamId)
    return raw.flatMap((h) => h.events.map((eid) => ({ quarter: h.quarter, eid }))).reverse()
  }, [historyQ.data, teamId])

  const count = rows.length

  // Persisted "last seen" count.
  const storageKey = `events-seen:${sessionId}:${teamId}`
  const [lastSeen, setLastSeen] = useState<number>(() => {
    if (typeof window === 'undefined') return 0
    const v = window.localStorage.getItem(storageKey)
    return v ? parseInt(v, 10) : 0
  })
  const unread = Math.max(0, count - lastSeen)

  const [open, setOpen] = useState(false)
  const openDrawer = () => {
    setOpen(true)
    setLastSeen(count)
    window.localStorage.setItem(storageKey, String(count))
  }

  // --- Toasts: pushed whenever count increases and drawer is closed. -------
  interface ToastItem { id: number; quarter: number; eid: string }
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const prevCountRef = useRef<number | null>(null)
  const toastIdRef = useRef(0)

  useEffect(() => {
    const prev = prevCountRef.current
    prevCountRef.current = count
    if (prev === null) return // skip first render — don't toast history on mount
    if (count <= prev) return
    if (open) return // don't toast while drawer is open; user is already looking
    const newRows = rows.slice(0, count - prev) // newest-first
    const fresh = newRows.map((r) => ({
      id: ++toastIdRef.current,
      quarter: r.quarter,
      eid: r.eid,
    }))
    setToasts((t) => [...fresh, ...t])
  }, [count, open, rows])

  const dismissToast = (id: number) =>
    setToasts((t) => t.filter((x) => x.id !== id))

  return (
    <>
      <Badge count={count} unread={unread} onClick={openDrawer} />
      <Drawer open={open} onClose={() => setOpen(false)} rows={rows} byId={byId} />
      <ToastStack toasts={toasts} byId={byId} onDismiss={dismissToast} onOpenAll={openDrawer} />
    </>
  )
}

// --- Badge ----------------------------------------------------------------

function Badge({
  count, unread, onClick,
}: { count: number; unread: number; onClick: () => void }) {
  const hasUnread = unread > 0
  return (
    <button
      onClick={onClick}
      aria-label={`Events: ${count} total, ${unread} unread`}
      className={
        'relative inline-flex items-center gap-2 rounded-full border px-3 py-1.5 ' +
        'text-sm font-medium transition-colors ' +
        (hasUnread
          ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200'
          : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50')
      }
    >
      <span aria-hidden>📰</span>
      <span>Events</span>
      <span
        className={
          'inline-flex items-center justify-center rounded-full min-w-[1.25rem] h-5 px-1 text-xs ' +
          (hasUnread ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-700')
        }
      >
        {count}
      </span>
      {hasUnread && (
        <span
          aria-hidden
          className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-white animate-ping"
        />
      )}
    </button>
  )
}

// --- Drawer ---------------------------------------------------------------

interface Row { quarter: number; eid: string }

function Drawer({
  open, onClose, rows, byId,
}: {
  open: boolean; onClose: () => void
  rows: Row[]; byId: Record<string, EventInfo>
}) {
  // Close on Esc
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={
          'fixed inset-0 bg-slate-900/30 transition-opacity z-40 ' +
          (open ? 'opacity-100' : 'opacity-0 pointer-events-none')
        }
        aria-hidden={!open}
      />
      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Events timeline"
        aria-hidden={!open}
        className={
          'fixed top-0 right-0 h-full w-full max-w-md bg-amber-50 shadow-xl z-50 ' +
          'flex flex-col border-l border-amber-200 ' +
          'transition-transform duration-300 ease-in-out ' +
          (open ? 'translate-x-0' : 'translate-x-full')
        }
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
          <h2 className="text-base font-semibold text-amber-900">
            📰 Events timeline
          </h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-800 text-xl leading-none"
            aria-label="Close events timeline"
          >
            ×
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {rows.length === 0 && (
            <p className="text-sm text-slate-500 italic">
              No events have fired yet this game.
            </p>
          )}
          {rows.map(({ quarter, eid }, i) => {
            const ev = byId[eid]
            if (!ev) return null
            return (
              <div key={`${quarter}-${eid}-${i}`} className="border-l-4 border-amber-400 pl-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-mono text-amber-700">Q{quarter}</span>
                  <span className="font-semibold text-sm">{ev.name}</span>
                </div>
                <p className="text-sm text-slate-700 italic mt-0.5">"{ev.narrative}"</p>
                {ev.response_hint && (
                  <p className="text-xs text-slate-600 mt-1">
                    <span className="font-medium">Response:</span> {ev.response_hint}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      </aside>
    </>
  )
}

// --- Toasts ---------------------------------------------------------------

const TOAST_DURATION_MS = 6000

function ToastStack({
  toasts, byId, onDismiss, onOpenAll,
}: {
  toasts: { id: number; quarter: number; eid: string }[]
  byId: Record<string, EventInfo>
  onDismiss: (id: number) => void
  onOpenAll: () => void
}) {
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none">
      {toasts.map((t) => (
        <Toast
          key={t.id}
          toast={t}
          ev={byId[t.eid]}
          onDismiss={() => onDismiss(t.id)}
          onOpenAll={() => { onDismiss(t.id); onOpenAll() }}
        />
      ))}
    </div>
  )
}

function Toast({
  toast, ev, onDismiss, onOpenAll,
}: {
  toast: { quarter: number; eid: string }
  ev: EventInfo | undefined
  onDismiss: () => void
  onOpenAll: () => void
}) {
  useEffect(() => {
    const t = setTimeout(onDismiss, TOAST_DURATION_MS)
    return () => clearTimeout(t)
  }, [onDismiss])

  if (!ev) return null
  return (
    <div
      role="status"
      className="pointer-events-auto rounded-lg border border-amber-300 bg-amber-50 shadow-lg p-3"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-mono text-amber-700">Q{toast.quarter}</span>
          <span className="font-semibold text-sm">{ev.name}</span>
        </div>
        <button
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-700 text-lg leading-none"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
      <p className="text-xs text-slate-700 italic mt-1 line-clamp-3">"{ev.narrative}"</p>
      <button
        onClick={onOpenAll}
        className="mt-2 text-xs font-medium text-amber-800 hover:underline"
      >
        View all events →
      </button>
    </div>
  )
}
